/**
 * Navigation utilities for hash-based scrolling
 * 
 * This module provides utilities for handling hash navigation that works
 * alongside React Router's path-based routing.
 * 
 * Future routes like /folderid/messageid/ won't conflict because:
 * - Hash navigation is only enabled on specific pages (landing page)
 * - React Router handles path segments separately from hash fragments
 * - URLs like /folderid/messageid/#section work fine (route + hash)
 */

/**
 * List of paths that support hash-based section navigation
 * Add paths here if you want hash navigation on other pages in the future
 */
const HASH_NAVIGATION_ENABLED_PATHS = [
    '/', // Landing page
    // Add other paths here if needed, e.g.:
    // '/blog/:slug', // If you want hash nav on blog posts
];

/**
 * Check if the current pathname supports hash navigation
 * @param {string} pathname - Current pathname from React Router
 * @returns {boolean} - True if hash navigation should be enabled
 */
export const isHashNavigationEnabled = (pathname) => {
    // Exact match for root path
    if (pathname === '/') {
        return true;
    }
    
    // For future: check against patterns (e.g., /blog/:slug)
    // This allows flexibility for routes like /blog/my-post#comments
    
    return HASH_NAVIGATION_ENABLED_PATHS.some(pattern => {
        // Simple exact match for now
        if (pattern === pathname) {
            return true;
        }
        
        // Future: add pattern matching for dynamic routes
        // if (pattern.includes(':')) {
        //     const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
        //     return regex.test(pathname);
        // }
        
        return false;
    });
};

/**
 * Check if we're on the landing page specifically
 * Useful for navigation that should redirect to landing page
 * @param {string} pathname - Current pathname from React Router
 * @returns {boolean} - True if on landing page
 */
export const isLandingPage = (pathname) => {
    return pathname === '/';
};

/**
 * Utility function to scroll to a section with offset for fixed headers
 * @param {string} sectionId - ID of the section to scroll to
 * @param {number} offset - Scroll offset in pixels (default: 80)
 * @returns {boolean} - True if scroll was successful
 */
export const scrollToSection = (sectionId, offset = 80) => {
    const targetSection = document.getElementById(sectionId);
    if (!targetSection) {
        console.warn(`Section with id "${sectionId}" not found`);
        return false;
    }

    const elementPosition = targetSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
    });

    return true;
};

/**
 * Build a URL with hash while preserving query parameters
 * @param {string} pathname - Current pathname
 * @param {string} search - Current search query string (e.g., ?referral=ABC123)
 * @param {string} hash - Hash fragment (e.g., #pricing)
 * @returns {string} - Complete URL string
 */
export const buildUrlWithHash = (pathname, search, hash) => {
    const hashStr = hash.startsWith('#') ? hash : `#${hash}`;
    return `${pathname}${search || ''}${hashStr}`;
};

